import React, { useState } from "react"
import { toast } from "react-toastify"
import { clsx } from "clsx"

import { AffiliateCommissionMode } from "services/user/type"
import { userService } from "services/user/user.service"

import { formatFiatCurrency } from "util/formatter"

import { UserAffiliateBulkEditor } from "./UserAffiliateBulkEditor"
import { UserAffiliateTable } from "./UserAffiliateTable"

export type AffiliateRow = {
  id: string
  code: string
  commission_mode: AffiliateCommissionMode
  return_percent: number | null
  cpa_config?: any | null
  balance: number
  total_earned: number
  estimated_unreleased?: number
  created_at: string
}

type Props = {
  userId?: string
  affiliates: AffiliateRow[]
  onUpdated: () => void
}

export const UserAffiliateTab = ({ userId, affiliates, onUpdated }: Props) => {
  const [releasing, setReleasing] = useState(false)

  const hasNgr = affiliates.some((a) => a.commission_mode === AffiliateCommissionMode.NGR)
  const canRelease = !!userId && hasNgr && !releasing

  const release = async () => {
    if (!userId) return
    try {
      setReleasing(true)
      await userService.releaseAffiliateNgrEarnings(userId)
      toast.success("Released NGR earnings up to now")
      onUpdated()
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to release earnings")
    } finally {
      setReleasing(false)
    }
  }

  const totalEstimatedUnreleased = affiliates.reduce((acc, a) => acc + (a.estimated_unreleased ?? 0), 0)

  return (
    <div className="flex flex-col gap-6 pt-6">
      <div className="rounded-xl border border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <div className="text-base font-semibold">NGR earnings</div>
            <div className="text-sm opacity-60">
              Releases this month’s accrued NGR commissions for this user up to now (safe to run multiple times).
            </div>
          </div>

          <div className="flex flex-col gap-2 items-end">
            <button
              type="button"
              className={clsx("btn btn-primary btn-sm w-fit", { "btn-disabled": !canRelease })}
              onClick={release}
              disabled={!canRelease}
            >
              {releasing ? <span className="loading loading-spinner loading-xs" /> : null}
              Release earnings
            </button>
            {hasNgr ? (
              <div className="mt-2 text-xs opacity-80">
                Estimated unreleased:{" "}
                <span className="font-semibold">{totalEstimatedUnreleased ? formatFiatCurrency(totalEstimatedUnreleased) : 0} USD</span>
              </div>
            ) : null}
          </div>
        </div>

        {!hasNgr ? <div className="mt-3 text-sm opacity-60">No affiliate codes in NGR mode.</div> : null}
      </div>

      <UserAffiliateBulkEditor userId={userId} onUpdated={onUpdated} />
      <UserAffiliateTable userId={userId} affiliates={affiliates} onUpdated={onUpdated} />
    </div>
  )
}
